import { omit } from "lodash-es";
import { Action } from "redux";
import {
  all,
  call,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
  takeLeading,
} from "redux-saga/effects";
import { isActionOf } from "typesafe-actions";

import { parseAreaType, TerritoryLevel } from "@mapmycustomers/shared";
import BoundaryDataSet from "@mapmycustomers/shared/enum/boundaryLegends/BoundaryDataSet";
import BoundaryLegendType from "@mapmycustomers/shared/enum/boundaryLegends/BoundaryLegendType";
import BoundaryType from "@mapmycustomers/shared/enum/BoundaryType";
import Color from "@mapmycustomers/shared/enum/Color";
import CountryCode from "@mapmycustomers/shared/enum/CountryCode";
import CountryRegion from "@mapmycustomers/shared/enum/CountryRegion";
import Currency from "@mapmycustomers/shared/types/Currency";
import { Territory } from "@mapmycustomers/shared/types/entity";
import BoundaryLegend from "@mapmycustomers/shared/types/map/BoundaryLegend";
import BoundaryStatsResponse, {
  BoundaryStatsThematicData,
} from "@mapmycustomers/shared/types/map/BoundaryStatsResponse";
import Organization from "@mapmycustomers/shared/types/Organization";
import AreaRegion from "@mapmycustomers/shared/types/territory/AreaRegion";
import LookupData from "@mapmycustomers/shared/types/territory/LookupData";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import {
  isNothingBoundaryLegend,
  isTerritoryColorBoundaryLegend,
} from "@mapmycustomers/shared/util/boundaryLegends/asserts";
import regionStates from "@mapmycustomers/shared/util/consts/regionStates";
import parseCountryState from "@mapmycustomers/shared/util/parseCountryState";

import { callApi } from "@app/store/api/callApi";
import { getAllBoundaryLegends } from "@app/store/boundaryLegends";
import { fetchBoundaryLegends, updateBoundaryLegend } from "@app/store/boundaryLegends/actions";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";
import { updateMetadata } from "@app/store/iam/actions";
import BoundaryViewState from "@app/store/mapLayers/boundaries/types/BoundaryViewState";
import ClassificationMethod from "@app/store/mapLayers/boundaries/types/ClassificationMethod";
import { getBoundariesLayerData } from "@app/store/mapLayers/selectors";
import { getTerritories } from "@app/store/territories";
import { fetchAllTerritories } from "@app/store/territories/actions";
import DemographicData, { DemographicMetadata } from "@app/types/map/DemographicData";
import classificationMethodMap from "@app/util/map/classificationMethodMap";
import { levelToRegion } from "@app/util/map/getLevelData";
import { lookupDataService } from "@app/util/map/LookupDataService";

import {
  boundariesViewSettingsChanged,
  ensureDemographicDataLoaded,
  fetchBoundaryStats,
  initializeTerritories,
  setBoundaryType,
  setCountryRegions,
  setZoomLevel,
  toggleLayerLegendExpandedState,
  toggleLayerVisibility,
} from "../actions";

import {
  getBoundaryType,
  getBoundaryViewStates,
  getCountryRegions,
  getDemographicData,
  getDemographyDataCountryCode,
} from "./selectors";
import BoundariesLayerData, { RegionDemographicData } from "./types/BoundariesLayerData";
import Bucket from "./types/Bucket";
import calculateBucketsForMethod from "./util/calculateBucketsForMethod";

const MAXIMUM_RESPONSE_COUNT = 10000;

function* onPersistBoundariesSettings() {
  const layerData: BoundariesLayerData = yield select(getBoundariesLayerData);
  yield put(
    updateMetadata.request({
      boundariesSettings: omit(layerData, [
        "demographicData",
        "territoriesLoading",
        "boundaryViewStates",
        "boundaryStats",
        "countryRegions",
      ]),
    })
  );
}

function* onInitializeTerritories() {
  try {
    yield put(fetchAllTerritories.request());
    const { failure } = yield race({
      failure: take([fetchAllTerritories.failure]),
      success: take([fetchAllTerritories.success]),
    });

    // fail app initialization if any of fetch actions failed, and succeed otherwise
    if (failure) {
      yield put(initializeTerritories.failure());
      return;
    }

    yield put(initializeTerritories.success());
  } catch (error) {
    yield put(initializeTerritories.failure());
    yield put(handleError({ error }));
  }
}

function* onEnsureDemographicDataLoaded({
  payload: countryCode,
}: ReturnType<typeof ensureDemographicDataLoaded.request>) {
  try {
    const demographyDataCountryCode: CountryCode = yield select(getDemographyDataCountryCode);

    const regionData: RegionDemographicData = yield select(getDemographicData);
    if (regionData && demographyDataCountryCode === countryCode) {
      yield put(ensureDemographicDataLoaded.success({}));
      return;
    }
    const demographicData: {
      fips: DemographicData[];
      state: DemographicData[];
      zip: DemographicData[];
    } = {
      fips: [],
      state: [],
      zip: [],
    };

    const requestPayload = {
      $limit: MAXIMUM_RESPONSE_COUNT,
      $offset: 0,
    };
    const data = [];
    const response: ListResponse<DemographicData, DemographicMetadata> = yield callApi(
      "fetchDemographicData",
      {
        ...requestPayload,
        $filters: { countryCode },
      }
    );
    data.push(...response.data);
    const apiCalls = [];
    if (response.total > MAXIMUM_RESPONSE_COUNT) {
      for (let i = 1; i <= Math.floor(response.total / MAXIMUM_RESPONSE_COUNT); i += 1) {
        apiCalls.push(
          callApi("fetchDemographicData", {
            ...requestPayload,
            $filters: { countryCode },
            $offset: MAXIMUM_RESPONSE_COUNT * i,
          })
        );
      }
    }
    const remainingResponse: ListResponse<DemographicData>[] = yield all(apiCalls);
    remainingResponse.forEach((res) => {
      data.push(...res.data);
    });
    data.forEach((item) => {
      if (item.type === "zip") {
        demographicData.zip.push(item);
      } else if (item.type === "fips") {
        demographicData.fips.push(item);
      } else if (item.type === "state") {
        demographicData.state.push(item);
      }
    });
    const result = {
      fips: demographicData.fips.reduce<Record<DemographicData["code"], DemographicData>>(
        (result, { code, income, population, type }) => {
          result[code] = { code, income, population, type };
          return result;
        },
        {}
      ),
      state: demographicData.state.reduce<Record<DemographicData["code"], DemographicData>>(
        (result, { code, income, population, type }) => {
          result[code] = { code, income, population, type };
          return result;
        },
        {}
      ),
      zip: demographicData.zip.reduce<Record<DemographicData["code"], DemographicData>>(
        (result, { code, income, population, type }) => {
          result[code] = { code, income, population, type };
          return result;
        },
        {}
      ),
    };
    yield put(
      ensureDemographicDataLoaded.success({
        demographicData: result,
        demographyDataCountryCode: countryCode,
        metadata: response.metaData,
      })
    );
  } catch (error) {
    yield put(ensureDemographicDataLoaded.failure());
    yield put(handleError({ error }));
  }
}

function* onBoundaryKeyChange({ payload }: ReturnType<typeof boundariesViewSettingsChanged>) {
  const { boundaryKey } = payload;

  const territoryBoundaryKey = boundaryKey?.territory;
  const geographyBoundaryKey = boundaryKey?.geography;

  if (territoryBoundaryKey) {
    yield put(
      fetchBoundaryStats.request({
        boundaryLegendId: territoryBoundaryKey,
        boundaryType: BoundaryType.TERRITORY,
      })
    );
  }

  if (geographyBoundaryKey) {
    yield put(
      fetchBoundaryStats.request({
        boundaryLegendId: geographyBoundaryKey,
        boundaryType: BoundaryType.GEOGRAPHY,
      })
    );
  }
}

const findBucketIndex = (
  buckets: Bucket[],
  data: number,
  classificationMethod: ClassificationMethod
) => {
  return buckets.findIndex((bucket, index) => {
    return (
      (bucket.range[0] < data ||
        bucket.range[0] === 0 ||
        (classificationMethod === ClassificationMethod.NATURAL_BREAKS &&
          bucket.range[0] <= data)) &&
      ((bucket.range[1] && bucket.range[1] >= data) ||
        (!bucket.range[1] && (index + 1 >= buckets.length || buckets[index + 1].range[0] > data)))
    );
  });
};

const findRangeIndex = (
  ranges: BoundaryStatsThematicData["ranges"],
  data: number,
  classificationMethod: ClassificationMethod
) => {
  return ranges.findIndex((range, index) => {
    return (
      (range[0] < data ||
        range[0] === 0 ||
        (classificationMethod === ClassificationMethod.NATURAL_BREAKS && range[0] <= data)) &&
      ((range[1] && range[1] >= data) ||
        (!range[1] && (index + 1 >= ranges.length || ranges[index + 1][0] > data)))
    );
  });
};

function* onFetchBoundaryStats({ payload }: ReturnType<typeof fetchBoundaryStats.request>) {
  try {
    const { boundaryType, bounds } = payload;
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const boundaryViewStates: Record<BoundaryType, BoundaryViewState> =
      yield select(getBoundaryViewStates);
    const configuration: BoundaryViewState = boundaryViewStates[boundaryType];

    // ensure legends are loaded
    yield put(fetchBoundaryLegends.request());
    yield take([fetchBoundaryLegends.success, fetchBoundaryLegends.failure]);

    const legends: BoundaryLegend[] = yield select(getAllBoundaryLegends);
    const legend = legends.find(({ id }) => id === configuration.boundaryLegendId);

    const emptyResponse: ReturnType<typeof fetchBoundaryStats.success>["payload"] = {
      boundaryType,
      colors: [],
      data: [],
      ranges: [],
    };

    if (
      !configuration.boundaryLegendId ||
      !legend ||
      isTerritoryColorBoundaryLegend(legend) ||
      isNothingBoundaryLegend(legend)
    ) {
      yield put(fetchBoundaryStats.success(emptyResponse));
      return;
    }

    if (legend?.type === BoundaryLegendType.DEMOGRAPHIC) {
      // We don't mind using invalid keys as we're checking result anyway
      // @ts-ignore
      const method = classificationMethodMap[legend.thematic?.classification ?? ""];

      if (!method || !legend.thematic || !legend.country || !legend.thematic.colors?.length) {
        yield put(fetchBoundaryStats.success(emptyResponse));
        return;
      }

      // ensure demographic dataOrBreakpoints is loaded
      yield put(ensureDemographicDataLoaded.request(legend.country));
      yield take([ensureDemographicDataLoaded.success, ensureDemographicDataLoaded.failure]);

      const demographicData: RegionDemographicData | undefined = yield select(getDemographicData);
      if (!demographicData) {
        yield put(fetchBoundaryStats.success(emptyResponse));
        return;
      }

      let currencyId: Currency["id"] | undefined;

      if (boundaryType === BoundaryType.TERRITORY) {
        const territories: Territory[] = yield select(getTerritories);

        // we only analyze: a) visible territories b) non-manual territories c) which match legend's country
        const eligibleTerritories = territories.filter(({ territoryDetail }) => {
          if (territoryDetail.hidden || territoryDetail.type === "points") {
            return false;
          }
          const parsedAreaType = parseAreaType(territoryDetail.type);
          return parsedAreaType?.countryCode === legend.country;
        });

        // now we need to calculate data for each territory
        const data = eligibleTerritories.map((territory) => {
          // We verified that type is not "points" already
          // @ts-ignore
          const { level } = parseAreaType(territory.territoryDetail.type)!;
          const regionData = demographicData[levelToRegion[level]];
          return territory.territoryDetail[level].reduce((result, areaRegionId) => {
            const data = regionData[areaRegionId];
            const value = data
              ? legend.dataset === BoundaryDataSet.MEDIAN_INCOME
                ? data.income.value
                : data.population
              : 0;
            return result + value;
          }, 0);
        });

        const buckets = calculateBucketsForMethod(
          method,
          method === ClassificationMethod.MANUAL
            ? data.length + 1
            : Math.min(legend.thematic.colors?.length ?? 3, data.length),
          currencyId,
          method === ClassificationMethod.MANUAL ? (legend.thematic.breakPoints ?? []) : data,
          legend.thematic.colors.map((colorItem) => colorItem.color)
        );

        let maxValue: number | undefined;
        const statsData = eligibleTerritories.map((territory, index) => {
          const value = data[index];
          if (!isDefined(maxValue) || value > maxValue) {
            maxValue = value;
          }
          const rangeIndex = findBucketIndex(buckets, value, method);
          return {
            code: territory.id,
            rangeIndex: rangeIndex,
            stats: {
              color:
                rangeIndex >= 0
                  ? (buckets[rangeIndex].color ?? legend.thematic?.defaults.color ?? Color.BLACK)
                  : (legend.thematic?.defaults.color ?? Color.BLACK),
              value,
            },
          };
        });

        yield put(
          fetchBoundaryStats.success({
            boundaryType: boundaryType,
            colors: buckets.map((bucket) => ({ color: bucket.color })),
            data: statsData,
            ranges: buckets.map((bucket) =>
              bucket.range.length === 1
                ? [bucket.range[0], maxValue ?? bucket.range[0]]
                : bucket.range
            ),
          })
        );
        return;
      }

      const regionData = demographicData[configuration.level];

      let areaRegionIds = Object.keys(regionData);

      // now let's filter regionCodes by countryRegions
      const countryRegions: CountryRegion[] = yield select(getCountryRegions);
      // we don't need to work on data if method is manual, so we can skip this block entirely
      if (countryRegions.length && method !== ClassificationMethod.MANUAL) {
        const lookupData: LookupData = yield call(
          [lookupDataService, lookupDataService.getLookupDataPromise],
          legend.country
        );

        const visibleRegions = new Set<AreaRegion["id"]>(
          countryRegions
            .flatMap((countryRegion) => regionStates[countryRegion])
            .flatMap((countryState) => {
              const parsedCountryState = parseCountryState(countryState);
              if (legend.country === parsedCountryState.countryCode) {
                const stateId: AreaRegion["id"] = parsedCountryState.areaRegionId;
                if (configuration.level === "state") {
                  return [stateId];
                } else if (configuration.level === "fips") {
                  return lookupData[TerritoryLevel.ADM1][stateId][TerritoryLevel.ADM2];
                } else if (configuration.level === "zip") {
                  return lookupData[TerritoryLevel.ADM1][stateId][TerritoryLevel.ADM3];
                }
              }
              return [];
            })
        );

        areaRegionIds = areaRegionIds.filter((regionId) => visibleRegions.has(regionId));
      }

      const dataOrBreakpoints =
        method === ClassificationMethod.MANUAL
          ? (legend.thematic.breakPoints ?? [])
          : areaRegionIds.map((areaRegionId: AreaRegion["id"]) => {
              if (legend.dataset === BoundaryDataSet.MEDIAN_INCOME) {
                if (!currencyId) {
                  currencyId = regionData[areaRegionId].income.currencyId;
                }
                return regionData[areaRegionId].income.value;
              } else {
                return regionData[areaRegionId].population;
              }
            });

      const buckets = calculateBucketsForMethod(
        method,
        method === ClassificationMethod.MANUAL
          ? dataOrBreakpoints.length + 1
          : Math.min(legend.thematic.colors?.length ?? 3, dataOrBreakpoints.length),
        currencyId,
        dataOrBreakpoints,
        legend.thematic.colors.map((colorItem) => colorItem.color)
      );

      let maxValue: number | undefined = undefined;
      const statsData = areaRegionIds.map((code) => {
        const value =
          legend.dataset === BoundaryDataSet.MEDIAN_INCOME
            ? regionData[code].income.value
            : regionData[code].population;
        if (!isDefined(maxValue) || value > maxValue) {
          maxValue = value;
        }
        const index = findBucketIndex(buckets, value, method);
        return {
          code,
          rangeIndex: index,
          stats: {
            color:
              index >= 0
                ? (buckets[index].color ?? legend.thematic?.defaults.color ?? Color.BLACK)
                : (legend.thematic?.defaults.color ?? Color.BLACK),
            value,
          },
        };
      });

      yield put(
        fetchBoundaryStats.success({
          boundaryType: boundaryType,
          colors: buckets.map((bucket) => ({ color: bucket.color })),
          data: statsData,
          ranges: buckets.map((bucket) =>
            bucket.range.length === 1
              ? [bucket.range[0], maxValue ?? bucket.range[0]]
              : bucket.range
          ),
        })
      );
    } else {
      const response: BoundaryStatsResponse = yield callApi("fetchBoundaryStats", orgId, {
        boundaryLegendId: configuration.boundaryLegendId!,
        bounds,
        level: boundaryType === BoundaryType.TERRITORY ? "territory" : configuration.level,
      });

      // We don't mind using invalid keys as we're checking result anyway
      // @ts-ignore
      const method = classificationMethodMap[response.thematic?.classification ?? ""];

      const statsData = response.data.map((stat) => {
        const rangeIndex = findRangeIndex(
          response.thematic?.ranges ?? [],
          stat.stats.value,
          method ?? ClassificationMethod.NATURAL_BREAKS
        );
        return { ...stat, rangeIndex };
      });

      yield put(
        fetchBoundaryStats.success({
          boundaryType: boundaryType,
          colors: response.thematic?.colors ?? [],
          data: statsData,
          ranges: response.thematic?.ranges ?? [],
        })
      );
    }
  } catch (error) {
    yield put(fetchBoundaryStats.failure());
    yield put(handleError({ error }));
  }
}

function* onZoomLevelChange({ payload }: ReturnType<typeof setZoomLevel>) {
  const currentBoundaryType: BoundaryType = yield select(getBoundaryType);

  const boundaryViewStates: Record<BoundaryType, BoundaryViewState> =
    yield select(getBoundaryViewStates);
  const configuration: BoundaryViewState = boundaryViewStates[currentBoundaryType];

  if (levelToRegion[payload] !== configuration.level) {
    yield put(
      fetchBoundaryStats.request({
        boundaryType: currentBoundaryType,
        level: levelToRegion[payload],
      })
    );
  }
}

function* onCountryRegionsChange() {
  const currentBoundaryType: BoundaryType = yield select(getBoundaryType);
  // re-calculate buckets only for the visible regions
  yield put(fetchBoundaryStats.request({ boundaryType: currentBoundaryType }));
}

function* onBoundaryLegendUpdated({ payload }: ReturnType<typeof updateBoundaryLegend.success>) {
  const boundaryViewStates: Record<BoundaryType, BoundaryViewState> =
    yield select(getBoundaryViewStates);

  yield all(
    Object.values(BoundaryType)
      .filter((boundaryType) => boundaryViewStates[boundaryType].boundaryLegendId === payload.id)
      // reset hidden ranges in case the number of buckets changed
      .map((boundaryType) => put(fetchBoundaryStats.request({ boundaryType, resetRanges: true })))
  );
}

const isToggleVisibilityOrLegendAction = isActionOf([
  toggleLayerVisibility,
  toggleLayerLegendExpandedState,
]);

const fetchTerritoryBoundaryStatsAction = (action: Action): boolean =>
  isActionOf(fetchBoundaryStats.request, action) &&
  action.payload.boundaryType === BoundaryType.TERRITORY;

const fetchGeographyBoundaryStatsAction = (action: Action): boolean =>
  isActionOf(fetchBoundaryStats.request, action) &&
  action.payload.boundaryType === BoundaryType.GEOGRAPHY;

export function* boundariesLayerSagas() {
  yield takeLatest(initializeTerritories.request, onInitializeTerritories);
  yield takeLeading(ensureDemographicDataLoaded.request, onEnsureDemographicDataLoaded);
  yield takeLatest(
    [
      (action: Action) =>
        isToggleVisibilityOrLegendAction(action) && action.payload.name === "boundaries",
      setBoundaryType,
      fetchBoundaryStats.request,
      ensureDemographicDataLoaded.success,
    ],
    onPersistBoundariesSettings
  );
  yield takeEvery(boundariesViewSettingsChanged, onBoundaryKeyChange);
  yield takeLatest(fetchTerritoryBoundaryStatsAction, onFetchBoundaryStats);
  yield takeLatest(fetchGeographyBoundaryStatsAction, onFetchBoundaryStats);
  yield takeLatest(updateBoundaryLegend.success, onBoundaryLegendUpdated);
  yield takeLatest(setZoomLevel, onZoomLevelChange);
  yield takeLatest(setCountryRegions, onCountryRegionsChange);
}
